import React, { useMemo } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
} from "date-fns";
import { Thumbnail } from "../Thumbnail";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@exiftrator/shared/components/ui";
import { useIsMobile, useIsTablet } from "../../hooks/useMobile";
import {
  WORKSPACE_SPACING,
  getDensityClass,
} from "../Workspace/workspaceSpacing";
import { getMonthlyCellDisplay } from "./monthlyCellDisplay";

const CalendarCell = React.memo(
  ({
    day,
    dateKey,
    isCurrentMonth,
    isDragOver,
    hasFiles,
    groupedFiles,
    onCellClick,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileDragStart,
    isMobile,
    isTablet,
  }) => {
    // Mobile: show dot indicator instead of thumbnails
    // Tablet: show max 2 thumbnails
    // Desktop: show max 4 thumbnails
    const maxVisible = isMobile ? 0 : isTablet ? 2 : 4;
    const displayFiles = useMemo(
      () => groupedFiles.slice(0, maxVisible),
      [groupedFiles, maxVisible],
    );
    const moreCount = groupedFiles.length - maxVisible;
    const isToday =
      format(day, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
    const {
      showFileIndicators,
      showThumbnails,
      showAccentBorder,
      mediaToneClass,
    } = getMonthlyCellDisplay({
      hasFiles,
      isCurrentMonth,
      isMobile,
    });

    // Responsive cell height
    const cellHeight = isMobile ? "min-h-[48px]" : isTablet ? "min-h-[80px]" : "min-h-[120px]";
    const cellPadding = isMobile ? "p-1" : "p-2";

    return (
      <div
        className={`${cellHeight} ${cellPadding} relative cursor-pointer transition-all flex flex-col group ${
          isCurrentMonth
            ? hasFiles
              ? "bg-bg-secondary"
              : "bg-bg-secondary/50"
            : "bg-bg-primary/40 select-none"
        } ${isToday && isCurrentMonth ? "bg-accent-primary-glow" : ""} ${
          isDragOver
            ? "bg-accent-primary-glow ring-2 ring-inset ring-accent-primary ring-dashed"
            : "hover:bg-bg-tertiary"
        } ${
          showAccentBorder ? "border-l-2 border-accent-primary-dim" : ""
        }`}
        onClick={(e) => onCellClick(day, e)}
        onDragOver={(e) => onDragOver(e, day)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, day)}
      >
        <div className="flex flex-col items-center mb-1">
          <div
            className={`font-semibold ${
              isToday
                ? "text-text-accent"
                : isCurrentMonth
                  ? "text-text-secondary"
                  : "text-text-supporting/50"
            } ${isMobile ? "text-[11px]" : "text-[13px]"}`}
          >
            {format(day, "d")}
          </div>
          {showFileIndicators && (
            <>
              {/* Mobile: colored dot indicator */}
              {isMobile && (
                <div
                  className={`mt-1 w-2 h-2 rounded-full bg-accent-primary ${mediaToneClass}`}
                />
              )}
              {/* Tablet/Desktop: file count text */}
              {!isMobile && (
                <div
                  className={`text-[10px] text-text-supporting mt-[2px] font-mono ${mediaToneClass}`}
                >
                  {groupedFiles.length} {groupedFiles.length === 1 ? "file" : "files"}
                </div>
              )}
            </>
          )}
        </div>

        {/* Thumbnail grid (hidden on mobile) */}
        {showThumbnails && (
          <TooltipProvider delayDuration={300}>
            <div className={`flex flex-col mt-auto w-full ${mediaToneClass}`}>
              <div className="grid grid-cols-2 gap-[2px]">
                {displayFiles.map(({ file, index }, i) => {
                  const isLastWithOverflow = i === maxVisible - 1 && moreCount > 0;
                  return (
                    <Tooltip key={index}>
                      <TooltipTrigger asChild>
                        <div
                          className="relative aspect-square"
                          draggable={true}
                          onDragStart={(e) => onFileDragStart(e, index)}
                        >
                          <Thumbnail
                            src={file.thumbnailUrl}
                            alt={file.name}
                            size="fill"
                          />
                          {isLastWithOverflow && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-[2px]">
                              <span className="text-white text-[10px] font-semibold">
                                +{moreCount}
                              </span>
                            </div>
                          )}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">
                        {file.name}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
          </TooltipProvider>
        )}
      </div>
    );
  },
  (prev, next) => {
    return (
      prev.dateKey === next.dateKey &&
      prev.isCurrentMonth === next.isCurrentMonth &&
      prev.isDragOver === next.isDragOver &&
      prev.hasFiles === next.hasFiles &&
      prev.groupedFiles === next.groupedFiles &&
      prev.onCellClick === next.onCellClick &&
      prev.isMobile === next.isMobile &&
      prev.isTablet === next.isTablet
    );
  },
);

function MonthlyGrid({
  currentMonth,
  grouped,
  dragOverTarget,
  onCellClick,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileDragStart,
}) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const generateCalendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const startDate = startOfWeek(start);
    const endDate = endOfWeek(end);

    const days = [];
    let currentDate = startDate;

    while (currentDate <= endDate) {
      days.push(currentDate);
      currentDate = addDays(currentDate, 1);
    }

    return days;
  }, [currentMonth]);

  const emptyArray = useMemo(() => [], []);

  const marginClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.calendarCanvasMargin,
  );

  // Day headers: mobile single letter, desktop 3-letter
  const dayHeaders = isMobile
    ? ["S", "M", "T", "W", "T", "F", "S"]
    : ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className={`${marginClass} border border-border-default overflow-hidden bg-bg-primary flex flex-col flex-1 rounded-sm`}>
      {/* Grid body containing both header and body */}
      <div className="flex-1 overflow-y-auto">
        {/* Header row - sticky to stay at top while scrolling */}
        <div className="grid grid-cols-7 gap-px bg-border-default sticky top-0 z-10 border-b border-border-default shrink-0">
          {dayHeaders.map((day) => (
            <div key={day} className="bg-bg-tertiary p-2 text-center">
              <div className="text-[10px] font-semibold text-text-supporting uppercase tracking-widest">
                {day}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px bg-border-default">
          {generateCalendarDays.map((day, index) => {
            const dateKey = format(day, "yyyy-MM-dd");
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isDragOver = dragOverTarget === dateKey;
            const groupedFiles = grouped.get(dateKey) || emptyArray;
            const hasFiles = groupedFiles.length > 0;

            return (
              <CalendarCell
                key={index}
                day={day}
                dateKey={dateKey}
                isCurrentMonth={isCurrentMonth}
                isDragOver={isDragOver}
                hasFiles={hasFiles}
                groupedFiles={groupedFiles}
                onCellClick={onCellClick}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onFileDragStart={onFileDragStart}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MonthlyGrid;
